import type { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import 'dayjs/locale/en';
import { isNil } from '@pct/ts-typeguard';
import isBetween from 'dayjs/plugin/isBetween';
import dayjs from 'dayjs';

dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(isBetween);

export const DateTimeUtils = {
  //nem használt jelenleg
  /*initializeDayjs(locale: string) {
    dayjs.locale(locale);
  },*/

  getLocaleDateFormat(): string {
    const localeDataObj = dayjs.localeData();
    if (isNil(localeDataObj)) {
      return 'YYYY-MM-DD';
    }

    let fmt: string | undefined;
    try {
      fmt = localeDataObj.longDateFormat('L');
    } catch {
      fmt = undefined;
    }
    return fmt ?? 'YYYY-MM-DD';
  },

  parseDateTime(value: string): Dayjs | null {
    if (isNil(value)) return null;

    const dateFormat = this.getLocaleDateFormat();

    const formats = [
      'YYYY-MM-DD',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY/MM/DD',
      'YYYY/MM/DD HH:mm',
      'YYYY/MM/DD HH:mm:ss',
      'L',
      'L LT',
      'L LTS',
      dateFormat,
      `${dateFormat} HH:mm`,
      `${dateFormat} HH:mm:ss`,
    ];

    for (const format of formats) {
      const parsed = dayjs(value, format, true);
      if (parsed.isValid() === true) return parsed;
    }

    const fallback = dayjs(value);
    return fallback.isValid() === true ? fallback : null;
  },

  mergeDateAndTime(base: Dayjs | null, date?: Dayjs | null, time?: Dayjs | null, useTime = true): Dayjs {
    let result = base?.isValid() === true ? base.utc() : dayjs().utc().hour(0).minute(0).second(0);

    if (!isNil(date)) {
      result = result.year(date.year()).month(date.month()).date(date.date());
    }

    if (!isNil(time) && useTime) {
      result = result.hour(time.hour()).minute(time.minute()).second(0);
    }

    if (!useTime) {
      result = result.hour(0).minute(0).second(0);
    }

    return result;
  },

  formatDateTime(value: dayjs.Dayjs | Date | null): string {
    if (isNil(value)) return '';

    const d = dayjs(value);
    if (d.isValid() === false) return '';

    const dateFmt = DateTimeUtils.getLocaleDateFormat();
    const timeFmt = 'HH:mm';

    return d.format(`${dateFmt} ${timeFmt}`);
  },

  parseDateTimeWithTimezone(isoString: string): dayjs.Dayjs {
    const isoStringWithOffsetRegex = /([+-]\d{2}:?\d{2}|Z)$/i;
    // Match standard ISO offsets at the end of the string (+HH:mm, -HHmm, or Z)
    const offsetMatch = isoStringWithOffsetRegex.exec(isoString);
    // Parse the date (Day.js handles the absolute Unix timestamp correctly)
    const parsedDate = dayjs(isoString);

    if (offsetMatch === null) {
      return parsedDate;
    }

    // Re-apply the extracted offset purely for display/formatting
    const offset = offsetMatch[0].toUpperCase() === 'Z' ? 0 : offsetMatch[0];

    return parsedDate.utcOffset(offset);
  },

  /** UTC idő + zónából helyi idő előállítása. */
  toZoneWallClock(instant: Date | null, zone: string): Date | null {
    if (isNil(instant)) return null;
    const d = dayjs(instant).tz(zone);
    return new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), 0, 0);
  },

  /** Helyi idő + zónából UTC idő előállítása. */
  fromZoneWallClock(wall: Date | null, zone: string, format = 'YYYY-MM-DD HH:mm'): Date | null {
    if (isNil(wall)) return null;
    return dayjs.tz(dayjs(wall).format(format), zone).toDate();
  },
};
